import { z } from 'zod';
import { cashFlowEntrySchema, type CashFlowEntry } from '@/lib/cashFlow';
import { PAYMENT_PLAN_MODES, type PaymentPlanMode } from '@progear/finance-core';

/**
 * Maksekokkulepped client (vendor payment plans). Talks to `/api/payment-plans`
 * and Zod-validates every payload at the boundary (anti-slop — external data is
 * never trusted). The API answers in camelCase, so the wire schema IS the
 * domain shape (loans.ts convention).
 *
 * Money maths policy (L9 + master rule): every schedule/interest derivation
 * lives in finance-core (`paymentPlanSchedule` / `paymentPlanTotals`) — this
 * module only carries integer cents across the wire; components only render.
 */

/** Estonian dropdown labels for the schedule modes (UI stays Estonian). */
export const PLAN_MODE_LABELS_ET: Record<PaymentPlanMode, string> = {
  equal_principal: 'Võrdsed põhiosad',
  fixed_principal: 'Fikseeritud põhiosa',
};

export const PLAN_STATUSES = ['active', 'closed'] as const;
export type PaymentPlanStatus = (typeof PLAN_STATUSES)[number];

export const PLAN_STATUS_LABELS_ET: Record<PaymentPlanStatus, string> = {
  active: 'aktiivne',
  closed: 'suletud',
};

export const PLAN_ORIGINS = ['invoices', 'legacy'] as const;
export type PaymentPlanOrigin = (typeof PLAN_ORIGINS)[number];

/** Display state of one schedule row (roster expand). `pre2026` = neither part
 *  ever materialized (the legacy pre-2026 floor swallowed the whole row). */
export type PlanRowStatus = 'paid' | 'partial' | 'due' | 'upcoming' | 'pre2026';

export interface PlanRowState {
  status: PlanRowStatus;
  /** Cents already settled on the row's materialized parts (stored figures
   *  selected off the live entry rows — gross minus outstanding; lib-level
   *  derivation per the loans convention, never in a component). */
  paidCents: number;
  /** Cents still open across the row's materialized parts. */
  outstandingCents: number;
}

export const PLAN_ROW_STATUS_META: Record<PlanRowStatus, { label: string; cls: string }> = {
  paid: { label: 'Tasutud', cls: 'border-[hsl(var(--income))]/30 bg-[hsl(var(--income))]/10 text-[hsl(var(--income))]' },
  partial: { label: 'Osaliselt', cls: 'border-amber-400/30 bg-amber-400/10 text-amber-300' },
  due: { label: 'Tähtaeg käes', cls: 'border-[hsl(var(--cost))]/30 bg-[hsl(var(--cost))]/10 text-[hsl(var(--cost))]' },
  upcoming: { label: 'Ootel', cls: 'border-border bg-muted/30 text-muted-foreground' },
  pre2026: { label: 'Enne 2026', cls: 'border-sky-400/30 bg-sky-400/10 text-sky-300' },
};

/**
 * Resolve one detail installment's display state from its LIVE part states
 * (approved canvas chips: Tasutud / Osaliselt / Tähtaeg käes / Ootel + the
 * legacy "Enne 2026" row). Both figures are selections over stored API cents
 * (no rate/VAT maths — L9); `today` is injected for deterministic tests.
 */
export function planInstallmentState(row: PlanDetailInstallment, today: string): PlanRowState {
  const parts = [row.principal, row.interest].filter(
    (p): p is PlanInstallmentPartState => p !== null,
  );
  if (parts.length === 0) {
    return { status: 'pre2026', paidCents: 0, outstandingCents: 0 };
  }
  let paidCents = 0;
  let outstandingCents = 0;
  for (const part of parts) {
    paidCents += part.grossCents - part.outstandingCents;
    outstandingCents += part.outstandingCents;
  }
  if (parts.every((p) => p.settled)) {
    return { status: 'paid', paidCents, outstandingCents };
  }
  if (paidCents > 0) {
    return { status: 'partial', paidCents, outstandingCents };
  }
  return { status: row.dueDate <= today ? 'due' : 'upcoming', paidCents, outstandingCents };
}

/** The KPI strip's current-month window ("<Kuu> kohustus") — pure date maths
 *  off the injected `today` (deterministic tests), label in Estonian. */
export interface MonthWindow {
  from: string;
  to: string;
  /** Capitalized Estonian month name, e.g. "Juuli". */
  label: string;
}

const MONTH_NAME_ET = new Intl.DateTimeFormat('et-EE', { month: 'long', timeZone: 'UTC' });

export function currentMonthWindow(today: string): MonthWindow {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const name = MONTH_NAME_ET.format(new Date(Date.UTC(year, month - 1, 1)));
  return {
    from: `${today.slice(0, 7)}-01`,
    to: `${today.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`,
    label: name.charAt(0).toUpperCase() + name.slice(1),
  };
}

/** Roster KPI strip figures — ONE lib-level derivation over the list API's
 *  stored cents (loans' `portfolioSummary` convention; components only render).
 *  Null when the roster is empty (the strip shows em-dashes). */
export interface PlansSummary {
  activeCount: number;
  /** Σ open cents across ACTIVE plans' materialized rows. */
  outstandingCents: number;
  /** Σ open interest cents across ACTIVE plans ("Intressikulu (jääk)"). */
  interestOutstandingCents: number;
}

export function plansSummary(plans: readonly PaymentPlanListItem[]): PlansSummary | null {
  if (plans.length === 0) {
    return null;
  }
  const active = plans.filter((p) => p.status === 'active');
  let outstandingCents = 0;
  let interestOutstandingCents = 0;
  for (const p of active) {
    outstandingCents += p.outstandingCents;
    interestOutstandingCents += p.interestOutstandingCents;
  }
  return { activeCount: active.length, outstandingCents, interestOutstandingCents };
}

/* ------------------------------------------------------------------ wire */

const scheduleTotalsSchema = z.object({
  principalCents: z.number().int(),
  interestCents: z.number().int(),
  totalCents: z.number().int(),
});

const legacyBlockSchema = z.object({
  originalTotalCents: z.number().int(),
  paidBeforeCents: z.number().int(),
  agreedOn: z.string().nullable(),
  reference: z.string().nullable(),
});

const planSchema = z.object({
  id: z.string(),
  supplier: z.string(),
  category: z.string(),
  mode: z.enum(PAYMENT_PLAN_MODES),
  monthlyRateBps: z.number().int(),
  totalCents: z.number().int(),
  termMonths: z.number().int().nullable(),
  fixedPrincipalCents: z.number().int().nullable(),
  firstDueDate: z.string(),
  origin: z.enum(PLAN_ORIGINS),
  legacy: legacyBlockSchema.nullable(),
  status: z.enum(PLAN_STATUSES),
  note: z.string().nullable(),
  createdAt: z.string(),
});

/** One roster row (`GET /payment-plans`) — the plan + its derived progress. */
const planListItemSchema = planSchema.extend({
  installmentCount: z.number().int(),
  coveredCount: z.number().int(),
  scheduleTotals: scheduleTotalsSchema,
  settledCents: z.number().int(),
  outstandingCents: z.number().int(),
  /** Open interest across the plan's materialized rows — the roster KPI's
   *  "Intressikulu (jääk)" source (summed server-side from the same view read). */
  interestOutstandingCents: z.number().int(),
  nextDueDate: z.string().nullable(),
});

/** The live settlement state of ONE materialized part (principal/interest) —
 *  null when the part never materialized (pre-2026 floor / zero-cent / protected). */
const partStateSchema = z.object({
  entryId: z.string(),
  expectedDate: z.string(),
  grossCents: z.number().int(),
  outstandingCents: z.number().int(),
  settled: z.boolean(),
});

const detailInstallmentSchema = z.object({
  seq: z.number().int(),
  dueDate: z.string(),
  principalCents: z.number().int(),
  interestCents: z.number().int(),
  totalCents: z.number().int(),
  principal: partStateSchema.nullable(),
  interest: partStateSchema.nullable(),
});

const coveredInvoiceSchema = z.object({
  entry: cashFlowEntrySchema,
  outstandingCents: z.number().int(),
});

const detailSchema = z.object({
  plan: planSchema,
  scheduleTotals: scheduleTotalsSchema,
  installments: z.array(detailInstallmentSchema),
  coveredInvoices: z.array(coveredInvoiceSchema),
});

const materializationSchema = z.object({
  upserted: z.number().int(),
  deletedStale: z.number().int(),
  protectedCount: z.number().int(),
});

const listResponseSchema = z.object({ plans: z.array(planListItemSchema) });
const writeResponseSchema = z.object({
  plan: planSchema,
  materialization: materializationSchema,
});
const deleteResponseSchema = z.object({
  deleted: z.boolean(),
  materialization: materializationSchema,
});

const markPaidResponseSchema = z.object({
  recordedCount: z.number().int(),
  recordedCents: z.number().int(),
  principalPaidCents: z.number().int(),
  allocations: z.array(z.object({ entryId: z.string(), amountCents: z.number().int() })),
  unallocatedCents: z.number().int(),
  planStatus: z.enum(PLAN_STATUSES),
  entries: z.array(cashFlowEntrySchema),
});

export type PaymentPlan = z.infer<typeof planSchema>;
export type PaymentPlanListItem = z.infer<typeof planListItemSchema>;
export type PaymentPlanDetail = z.infer<typeof detailSchema>;
export type PlanInstallmentPartState = z.infer<typeof partStateSchema>;
export type PlanDetailInstallment = z.infer<typeof detailInstallmentSchema>;
export type PlanCoveredInvoice = z.infer<typeof coveredInvoiceSchema>;
export type PlanMaterialization = z.infer<typeof materializationSchema>;
export type PlanWriteResult = z.infer<typeof writeResponseSchema>;
export type PlanDeleteResult = z.infer<typeof deleteResponseSchema>;
export type PlanMarkPaidResult = z.infer<typeof markPaidResponseSchema>;

/* ---------------------------------------------------------------- drafts */

/** One schedule row of a write body (1:1 the API installment shape). */
export interface PlanInstallmentDraft {
  seq: number;
  dueDate: string;
  principalCents: number;
  interestCents: number;
  totalCents: number;
}

/** Shared core of the create/update bodies (terms + the stored schedule). */
export interface PlanCoreDraft {
  supplier: string;
  category: string;
  mode: PaymentPlanMode;
  monthlyRateBps: number;
  firstDueDate: string;
  termMonths: number | null;
  fixedPrincipalCents: number | null;
  note: string | null;
  installments: PlanInstallmentDraft[];
}

/** Origin 'legacy': a pre-2026 agreement imported as a fact (L9 identity:
 *  originalTotalCents = paidBeforeCents + totalCents). */
export interface PlanLegacyDraft {
  originalTotalCents: number;
  paidBeforeCents: number;
  agreedOn: string | null;
  reference: string | null;
}

/** Create body — exactly ONE of `entryIds` (invoices plan) / `legacy`. */
export interface CreatePlanDraft extends PlanCoreDraft {
  totalCents: number;
  entryIds: string[] | null;
  legacy: PlanLegacyDraft | null;
}

/** Update body — origin + total are immutable (delete + recreate instead). */
export interface UpdatePlanDraft extends PlanCoreDraft {
  status: PaymentPlanStatus | null;
}

export interface MarkPlanPaidDraft {
  seqs: number[];
  paidOn: string | null;
  /** A PARTIAL payment on exactly one installment (interest settles first). */
  amountCents: number | null;
  idempotencyKey: string;
}

/* --------------------------------------------------------------- requests */

export interface PlanRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

function authHeaders(accessToken: string | undefined, base: Record<string, string> = {}): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

/** Pull the API error envelope's message out of a failed response so the
 *  operator sees WHY a save failed — falling back to the bare status. */
async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

/** The full agreement roster (plans table + KPI strip). */
export async function fetchPaymentPlans(options: PlanRequestOptions = {}): Promise<PaymentPlanListItem[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payment-plans`, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Maksekokkulepete laadimine ebaõnnestus: ${res.status}`));
  }
  return listResponseSchema.parse(await res.json()).plans;
}

/** One agreement with its live schedule state + covered invoices. */
export async function fetchPaymentPlan(id: string, options: PlanRequestOptions = {}): Promise<PaymentPlanDetail> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payment-plans/${encodeURIComponent(id)}`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Maksekokkuleppe laadimine ebaõnnestus: ${res.status}`));
  }
  return detailSchema.parse(await res.json());
}

export async function createPaymentPlan(
  draft: CreatePlanDraft,
  options: PlanRequestOptions = {},
): Promise<PlanWriteResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payment-plans`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(draft),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Kokkuleppe salvestamine ebaõnnestus: ${res.status}`));
  }
  return writeResponseSchema.parse(await res.json());
}

export async function updatePaymentPlan(
  id: string,
  draft: UpdatePlanDraft,
  options: PlanRequestOptions = {},
): Promise<PlanWriteResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payment-plans/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(draft),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Kokkuleppe muutmine ebaõnnestus: ${res.status}`));
  }
  return writeResponseSchema.parse(await res.json());
}

/** DELETE purges every materialized installment row (even PAID ones) and lifts
 *  the covered invoices' suppression — the caller must confirm-warn first. */
export async function deletePaymentPlan(id: string, options: PlanRequestOptions = {}): Promise<PlanDeleteResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payment-plans/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Kokkuleppe kustutamine ebaõnnestus: ${res.status}`));
  }
  return deleteResponseSchema.parse(await res.json());
}

/** "Märgi makstuks" over installment seqs — amounts recomputed server-side (L9). */
export async function markPlanInstallmentsPaid(
  id: string,
  draft: MarkPlanPaidDraft,
  options: PlanRequestOptions = {},
): Promise<PlanMarkPaidResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(
    `${baseUrl}/api/payment-plans/${encodeURIComponent(id)}/installments/mark-paid`,
    {
      method: 'POST',
      headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(draft),
    },
  );
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Makstuks märkimine ebaõnnestus: ${res.status}`));
  }
  return markPaidResponseSchema.parse(await res.json());
}

export type { CashFlowEntry };
